import {
    BuildingOfficeIcon,
    ChatBubbleLeftRightIcon,
    CubeTransparentIcon,
    GlobeAltIcon,
    LinkIcon,
    ShareIcon,
    SparklesIcon,
    SwatchIcon,
    TagIcon,
    UsersIcon,
    VariableIcon,
    HomeModernIcon,
    IdentificationIcon,
    RectangleGroupIcon,
    ChatBubbleOvalLeftEllipsisIcon,
    ShieldCheckIcon
} from "@heroicons/react/24/outline";
import React from "react";
import type { TFunction } from "i18next";
import { ACCOUNT_SECURITY_ROUTE } from "../../config/routePermissions";

interface SettingsMenuItem {
    link: string;
    icon: React.ReactNode;
    text: string;
    permission?: string;
    childRoutes?: string[];
    group?: string;
}

/** Sécurité du compte : réglage de l'utilisateur, ouvert sans permission d'établissement. */
export const ACCOUNT_SECURITY_LINK = ACCOUNT_SECURITY_ROUTE;

/**
 * Entrée « Mon compte » en tête des Paramètres, puis les réglages d'établissement filtrés par
 * permission sous leur propre titre de groupe (SubMenuTemplate n'affiche les titres que si
 * chaque entrée en porte un).
 */
function withAccountItems(
    items: SettingsMenuItem[],
    hasPermission: (permission: string) => boolean,
    t: TFunction,
    facilityGroup: string
): SettingsMenuItem[] {
    const accountItem: SettingsMenuItem = {
        link: ACCOUNT_SECURITY_LINK,
        icon: <ShieldCheckIcon className="w-5 h-5"/>,
        text: t('settings:menu.account_security'),
        group: t('settings:menu.group_account'),
    };
    const facilityItems = items
        .filter(item => !item.permission || hasPermission(item.permission))
        .map(item => ({...item, group: facilityGroup}));

    return [accountItem, ...facilityItems];
}

export function getGroupSettingsMenuItems(
    user: { is_admin: boolean },
    hasPermission: (permission: string) => boolean,
    t: TFunction
): SettingsMenuItem[] {
    const items: SettingsMenuItem[] = [
        {link: '/settings/facilities', icon: <BuildingOfficeIcon className="w-5 h-5"/>, text: t('settings:menu.my_group'), permission: 'facility.view'},
        {link: '/settings/group-children', icon: <HomeModernIcon className="w-5 h-5"/>, text: t('settings:menu.facilities'), permission: 'facility.view'},
        {link: '/settings/hotel-groups', icon: <RectangleGroupIcon className="w-5 h-5"/>, text: t('settings:menu.hotel_groups'), permission: 'facility.view'},
        {link: '/settings/facility-groups', icon: <ShareIcon className="w-5 h-5"/>, text: t('settings:menu.access_sharing'), permission: 'facility.view'},
        {link: '/settings/mapping-keys', icon: <VariableIcon className="w-5 h-5"/>, text: t('settings:menu.variables'), permission: 'settings.view'},
        {link: '/settings/users', icon: <UsersIcon className="w-5 h-5"/>, text: t('settings:menu.users'), permission: 'users.view', childRoutes: ['/settings/roles']},
        {link: '/settings/redirect', icon: <LinkIcon className="w-5 h-5"/>, text: t('settings:menu.url_shortener'), permission: 'settings.view'},
        {link: '/settings/tags', icon: <TagIcon className="w-5 h-5"/>, text: t('settings:menu.tags'), permission: 'tags.view'},
        {link: '/settings/ai', icon: <SparklesIcon className="w-5 h-5"/>, text: t('settings:menu.ai'), permission: 'facility.view'},
        {link: '/settings/connectivities', icon: <CubeTransparentIcon className="w-5 h-5"/>, text: t('settings:menu.connectivities'), permission: 'connectivities.view'},
        {link: '/settings/brandings', icon: <SwatchIcon className="w-5 h-5"/>, text: t('settings:menu.brandings'), permission: 'brandings.view'},
        {link: '/settings/public-pages', icon: <GlobeAltIcon className="w-5 h-5"/>, text: t('settings:menu.public_pages', 'Pages publiques'), permission: 'facility.view', childRoutes: ['/settings/public-pages/lock']},
        {
            link: '/settings/communications',
            icon: <ChatBubbleLeftRightIcon className="w-5 h-5"/>,
            text: t('settings:menu.communications'),
            permission: 'settings.view'
        },
        {
            link: '/settings/site-chat',
            icon: <ChatBubbleOvalLeftEllipsisIcon className="w-5 h-5"/>,
            text: t('settings:menu.site_chat'),
            permission: 'settings.view',
            childRoutes: ['/settings/live-chat']
        },
    ];

    return withAccountItems(items, hasPermission, t, t('settings:menu.group_group'));
}

export function getFacilitySettingsMenuItems(
    user: { is_admin: boolean },
    hasPermission: (permission: string) => boolean,
    t: TFunction,
    facility?: { admin_has_meta_messaging?: boolean } | null
): SettingsMenuItem[] {
    const items: SettingsMenuItem[] = [
        {link: '/settings/facilities', icon: <BuildingOfficeIcon className="w-5 h-5"/>, text: t('settings:menu.my_facility'), permission: 'facility.view'},
        {link: '/settings/identity', icon: <IdentificationIcon className="w-5 h-5"/>, text: t('settings:menu.identity'), permission: 'facility.view'},
        {link: '/settings/mapping-keys', icon: <VariableIcon className="w-5 h-5"/>, text: t('settings:menu.variables'), permission: 'settings.view'},
        {link: '/settings/users', icon: <UsersIcon className="w-5 h-5"/>, text: t('settings:menu.users'), permission: 'users.view', childRoutes: ['/settings/roles']},
        {link: '/settings/redirect', icon: <LinkIcon className="w-5 h-5"/>, text: t('settings:menu.url_shortener'), permission: 'settings.view'},
        {link: '/settings/tags', icon: <TagIcon className="w-5 h-5"/>, text: t('settings:menu.tags'), permission: 'tags.view'},
        {link: '/settings/ai', icon: <SparklesIcon className="w-5 h-5"/>, text: t('settings:menu.ai'), permission: 'facility.view'},
        {link: '/settings/connectivities', icon: <CubeTransparentIcon className="w-5 h-5"/>, text: t('settings:menu.connectivities'), permission: 'connectivities.view'},
        {link: '/settings/brandings', icon: <SwatchIcon className="w-5 h-5"/>, text: t('settings:menu.brandings'), permission: 'brandings.view'},
        {link: '/settings/public-pages', icon: <GlobeAltIcon className="w-5 h-5"/>, text: t('settings:menu.public_pages', 'Pages publiques'), permission: 'facility.view', childRoutes: ['/settings/public-pages/lock']},
        {link: '/settings/facility-groups', icon: <ShareIcon className="w-5 h-5"/>, text: t('settings:menu.access_sharing'), permission: 'facility.view'},
        {
            link: '/settings/communications',
            icon: <ChatBubbleLeftRightIcon className="w-5 h-5"/>,
            text: t('settings:menu.communications'),
            permission: 'settings.view'
        },
        {
            link: '/settings/site-chat',
            icon: <ChatBubbleOvalLeftEllipsisIcon className="w-5 h-5"/>,
            text: t('settings:menu.site_chat'),
            permission: 'settings.view',
            childRoutes: ['/settings/live-chat']
        },
    ];

    // Note : Meta Messaging est désormais accessible via le tab "Messenger & Instagram"
    // dans Communications (gated par admin_has_meta_messaging côté CommunicationsSettings).
    // L'entrée sidebar séparée a été retirée pour éviter la duplication.

    return withAccountItems(items, hasPermission, t, t('settings:menu.group_facility'));
}
